import {
  createAsyncThunk,
  createEntityAdapter,
  createSlice,
} from "@reduxjs/toolkit";
import { enqueueSnackbar } from "notistack"; // Assuming you are using notistack for notifications

import request from "../../utils/request";
import requestAI from "../../utils/request-ai";

// Create entity adapter
const interviewAdapter = createEntityAdapter();

const initialState = interviewAdapter.getInitialState({
  isLoading: false,
  error: null,
  totalInterivews: 0,
  interviewData: {},
});

// Async thunk for fetching all interviews
export const getAllInterivews = createAsyncThunk(
  "interview/getAllInterivews",
  async (
    { page = 1, query = "", filters = {}, take = 10, ...rest },
    { getState, rejectWithValue }
  ) => {
    try {
      const { auth } = getState();
      const company_id = auth.user?.company_id;

      const response = await request({
        url: "/results/get-all",
        method: "POST",
        data: { skip: page, take, query, ...filters, ...rest, company_id },
      });

      console.log("RESPONSE INTERVIEWS GET", response, page);

      return {
        page,
        data: response?.data?.data || [],
        count: response?.data?.count || 0,
      };
    } catch (error) {
      enqueueSnackbar(error?.message, {
        autoHideDuration: 3000,
        variant: "error",
      });
      return rejectWithValue(error?.message);
    }
  }
);

// Async thunk for getting a single interview
export const getSingleInterivew = createAsyncThunk(
  "interview/getSingleInterivew",
  async (id, { rejectWithValue }) => {
    try {
      const response = await request({
        url: `/interivews/by-id`,
        method: "GET",
        params: { interview_id: id },
      });

      return response.data;
    } catch (error) {
      enqueueSnackbar(error?.message, {
        autoHideDuration: 3000,
        variant: "error",
      });
      return rejectWithValue(error?.message);
    }
  }
);

// Async thunk for getting a single interview
export const getTranslationOfInterview = createAsyncThunk(
  "interview/getTranslationOfInterview",
  async (id, { rejectWithValue }) => {
    try {
      const response = await requestAI({
        url: `/result/translate_interview_result`,
        method: "POST",
        data: {
          result_id: id,
        },
        timeout: 300000, // 5 minutes
      });

      return response?.result;
    } catch (error) {
      console.log("error", error);
      enqueueSnackbar(error?.detail, {
        autoHideDuration: 3000,
        variant: "error",
      });
      return rejectWithValue(error?.detail);
    }
  }
);

// Async thunk for fetching reject interview
export const rejectInterview = createAsyncThunk(
  "interview/rejectInterview",
  async (
    { ids, job_post_id, filters = {}, query = "" },
    { rejectWithValue }
  ) => {
    try {
      let payload = { ids, job_post_id };

      if (ids === "all") {
        let isFilterApplied =
          filters && Object.keys(filters).length > 0 ? true : false;

        if (isFilterApplied) {
          payload = { ...payload, isFilterApplied, ...filters };
        }

        if (query) {
          payload = { ...payload, isFilterApplied: true, ...filters, query };
        }
      }

      const response = await request({
        url: "/interviews/reject",
        method: "PUT",
        data: payload,
      });

      enqueueSnackbar(response?.message, { variant: "success" });

      console.log("RESPONSE REJECT INTERVIEW RESPONSE", response);

      return ids === "all" ? [] : response?.data;
    } catch (error) {
      enqueueSnackbar(error?.message, {
        autoHideDuration: 3000,
        variant: "error",
      });
      return rejectWithValue(error?.message);
    }
  }
);

// Async thunk for fetching delete interivew
export const deleteInterview = createAsyncThunk(
  "interview/deleteInterivew",
  async (
    { ids, job_post_id, page, filters = {}, query = "" },
    { rejectWithValue }
  ) => {
    try {
      let payload = { ids, job_post_id };

      if (ids === "all") {
        let isFilterApplied =
          filters && Object.keys(filters).length > 0 ? true : false;

        if (isFilterApplied) {
          payload = query
            ? { ...payload, isFilterApplied, ...filters, query }
            : { ...payload, isFilterApplied, ...filters };
        }
      }

      const response = await request({
        url: "/interviews/delete",
        method: "DELETE",
        data: payload,
      });

      console.log("RESPONSE DELETE INTERVIEWS", response);

      return { ids: ids === "all" ? [] : ids, page };
    } catch (error) {
      enqueueSnackbar(error?.message, {
        autoHideDuration: 3000,
        variant: "error",
      });
      return rejectWithValue(error?.message);
    }
  }
);

// Slice
const interivewSlice = createSlice({
  name: "interview",
  initialState,
  extraReducers: (builder) => {
    builder
      .addCase(getAllInterivews.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(getAllInterivews.fulfilled, (state, action) => {
        state.isLoading = false;
        state.totalInterivews = action.payload.count;
        interviewAdapter.upsertMany(state, action.payload.data || []);
        state.interviewData = {
          ...state.interviewData,
          [action.payload.page]:
            action.payload.data?.length > 0
              ? action?.payload?.data?.map((d) => d?.id)
              : [],
        };
      })
      .addCase(getAllInterivews.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload || action.error.message;
      })
      .addCase(getSingleInterivew.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(getSingleInterivew.fulfilled, (state, action) => {
        state.isLoading = false;
        interviewAdapter.setOne(state, action.payload);
      })
      .addCase(getSingleInterivew.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload || action.error.message;
      })
      .addCase(getTranslationOfInterview.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(getTranslationOfInterview.fulfilled, (state, action) => {
        console.log("action.payload", action.payload);
        state.isLoading = false;
        interviewAdapter.updateOne(state, {
          id: action.payload._id,
          changes: action.payload,
        });
      })
      .addCase(getTranslationOfInterview.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload || action.error.message;
      })
      .addCase(rejectInterview.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(rejectInterview.fulfilled, (state, action) => {
        state.isLoading = false;
        const formatedData = action.payload.map((c) => ({
          id: c?.id,
          changes: c,
        }));
        interviewAdapter.updateMany(state, formatedData);
      })
      .addCase(rejectInterview.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload || action.error.message;
      })
      .addCase(deleteInterview.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(deleteInterview.fulfilled, (state, action) => {
        state.isLoading = false;
        if (action?.payload.page) {
          state.interviewData = {
            ...state.interviewData,
            [action.payload.page]: state.interviewData[
              action.payload.page
            ]?.filter((f) => f !== action.payload.ids[0]),
          };
        }
        interviewAdapter.removeMany(state, action.payload.ids);
      })
      .addCase(deleteInterview.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload || action.error.message;
      });
  },
});

// ACTION EXORT
// export const { clearPostCreationStates, editPostContent, clearPostFormData, updatePostFormData } = interivewSlice.actions;

// SELECTORS
export const {
  selectAll: selectAllInterivews,
  selectById: selectInterivewById,
  selectIds: selectInterivewIds,
} = interviewAdapter.getSelectors((state) => state.interview);

export const selectLoading = (state) => state.interview.isLoading;
export const selectError = (state) => state.interview.error;

export default interivewSlice.reducer;
